import { useState } from 'react'
import clsx from 'clsx'
import { useMediaQuery } from '@hotmart/space-lib/dist/hooks'
import { AuthStatus } from '@hotmart/space-lib/dist/enum'
import {
  BlockedCommentsTag,
  CommentAvatarList,
  PostComment,
  PostCommentInput,
  PostInteractionCounters,
  PostReactions
} from 'src/components'
import { CommentType } from 'src/components/postComments'
import { useUser } from 'src/hook/useUser'
import { ScreenSizes } from 'src/styles'
import { CommentProps } from 'src/components/postComments/components'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { Actions, Entities, EventNames, ScreenNames, ArrivedFrom } from 'src/utils/datahub/enums'
import { CommentOrigin } from 'src/client/enum'
import { useOptimizely } from 'src/hook/useOptimizely'
import { Action } from 'src/utils/datahubDefaultEvent'
import { useCustomEvent } from 'src/hook/useCustomEvent'
import { FeedEventNames, PostStatus } from 'src/hook/usePosts'
import i18n from 'src/i18n'
import { FeedCardFooterProps } from './feedCardFooter.types'
import { ViewComments } from './feedCardFooter.styles'

const FeedCardFooter = ({
  post,
  enableMentions,
  enableCommentSuggestion,
  enableCommentInput,
  onClickCommentCounter
}: FeedCardFooterProps) => {
  const [commentsCount, setCommentsCount] = useState(post.commentsCount ?? 0)
  const [newComment, setNewComment] = useState<CommentProps>()
  const { authStatus, isOwnerOrAdminOrModerator } = useUser()
  const { emitEvent } = useCustomEvent()
  const { sendDataHubConsume } = useSendDataHubConsume()
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.md})`)
  const { optimizely } = useOptimizely()

  const allowMedias = [CommentType.IMAGE, CommentType.DOCUMENT]
  const isCommentsEnabled = Boolean(post?.isCommentsEnabled)
  const showCommentInput = enableCommentInput && (isCommentsEnabled || isOwnerOrAdminOrModerator)
  const showCommentsCounter = Boolean(commentsCount > 0)
  const showCommentsStatus = showCommentsCounter || !isCommentsEnabled
  const showPostReactions = post?.postStatus !== PostStatus.SCHEDULED

  const handleCommentSuccess = (comment: CommentProps) => {
    setCommentsCount((prevState) => prevState + 1)
    setNewComment(comment)
    emitEvent(FeedEventNames.COMMENT_CREATED, {
      postId: Number(post.id)
    })
  }

  const handlePostReaction = ({ reactionCode }) => {
    sendDataHubConsume({
      action: Actions.VIEW,
      entity: Entities.COMMUNITY,
      event: {
        eventName: EventNames.TRY_TO_REACT_TO_A_POST,
        screenName: ScreenNames.COMMUNITY_POST,
        communityId: Number(post.ownerCollectionId),
        postId: Number(post.id),
        option: reactionCode,
        arrivedFrom: ArrivedFrom.COMMUNITY_FEED_V3
      }
    })

    optimizely.track(Action.TRY_TO_REACT_TO_A_POST)

    emitEvent(FeedEventNames.POST_REACTION, {
      postId: Number(post.id)
    })
  }

  return (
    <footer className="_border-top _border-gray-200">
      <div
        className={clsx(
          '_d-flex _align-items-start _align-items-md-center _justify-content-between _flex-column-reverse _flex-md-row',
          {
            '_p-4': !isMobile
          }
        )}
      >
        <PostInteractionCounters
          className={clsx({ '_p-4': isMobile })}
          comments={commentsCount}
          views={post?.viewsCount}
          shortNotation={isMobile}
          onClickCommentCounter={onClickCommentCounter}
        />

        {showPostReactions && (
          <PostReactions
            className={clsx({ '_w-full _p-4 _border-bottom _border-gray-200': isMobile })}
            postId={post.id}
            reactions={post?.reactions}
            disabled={authStatus !== AuthStatus.AUTHENTICATED}
            onReaction={handlePostReaction}
          />
        )}
      </div>

      {showCommentInput && (
        <>
          <div className="_border-top _border-gray-200 _p-4">
            <PostCommentInput
              idPost={post.id}
              allowMedias={allowMedias}
              authorRole={post.authorMemberType}
              authorName={post?.authorName}
              authorUcode={post?.authorUcode}
              enableMentions={enableMentions}
              enableBreakLine
              commentSuggestion={enableCommentSuggestion}
              commentOrigin={CommentOrigin.COMMUNITY_FEED}
              onSuccess={handleCommentSuccess}
            />
          </div>

          {newComment && (
            <div className="_border-top _border-gray-200 _px-6 _py-4">
              <PostComment comment={newComment} postId={post.id} />
            </div>
          )}
        </>
      )}

      {showCommentsStatus && (
        <div
          className={clsx(
            '_d-flex _align-items-start _align-items-md-center _border-top _border-gray-200 _flex-column-reverse _flex-md-row',
            {
              '_p-4': !isMobile
            }
          )}
        >
          {showCommentsCounter && (
            <CommentAvatarList
              users={post?.commentAuthorsPreview}
              commentsCount={commentsCount}
              hasUnseenMentionInComments={post?.hasUnseenMentionInComments}
              onClick={onClickCommentCounter}
              className={clsx({
                '_m-4': isMobile
              })}
            />
          )}

          <div
            className={clsx('_d-flex _w-full', {
              '_w-full _p-4': isMobile,
              '_border-bottom _border-gray-200': isMobile && showCommentsCounter
            })}
          >
            {isCommentsEnabled && (
              <ViewComments
                className="_rounded _cursor-pointer _px-4 _py-2 _ml-md-auto"
                onClick={onClickCommentCounter}
              >
                {i18n.t('feed.card.comments.show_comments')}
              </ViewComments>
            )}

            {!isCommentsEnabled && <BlockedCommentsTag className="_ml-md-auto" />}
          </div>
        </div>
      )}
    </footer>
  )
}

export default FeedCardFooter
